import Model from '@ember-data/model';
import attr from 'ember-data/attr';
import { belongsTo } from 'ember-data/relationships';

import AccountModel from 'cio/models/account';
import { EnvironmentUsage } from 'cio/utils/billing/usage';

export default class AccountUsageModel extends Model {
  @belongsTo('account', { async: false, inverse: null }) account!: AccountModel;

  @attr('boolean') includesAllEnvs!: boolean;
  @attr('number') highWatermarkCustomersTotal!: number;
  @attr('number') customersTotal!: number;
  @attr('number') customersAllotted!: number;
  @attr('number') highWatermarkObjectsTotal!: number;
  @attr('number') objectsTotal!: number;
  @attr('number') objectsAllotted!: number;
  @attr('number') messagesSent!: number;
  @attr('number') billableMessagesSent!: number;
  @attr('number') messagesAllotted!: number;
  @attr('number') cdpApiCalls!: number;
  @attr('number') cdpApiCallsAllotted!: number;
  @attr('number') cdpApiCurrentTierRate!: number;

  @attr('account/environment-usage', { defaultValue: () => [] }) environmentUsages!: EnvironmentUsage[];
  @attr() environments!: Record<string, number | { total: number }>;
  @attr() users!: Record<string, number | { total: number }>;
  @attr() collections!: Record<string, number | { total: number }>;
  @attr() warehouseSyncs!: Record<string, number | { total: number }>;

  get unbilledEnvironmentUsages() {
    return this.environmentUsages.filter((env) => !env.billed);
  }

  // The account usage object only represents billable workspaces, and not unbilled workspaces.
  // If all environments are billed, we can use this number. If some environments are
  // unbilled, however, we need to calculate the total number of people in the account by
  // summing all of the billable people per environment.
  get totalCustomerCount() {
    if (!this.unbilledEnvironmentUsages.length) {
      return this.customersTotal;
    }

    return this.environmentUsages.reduce((sum, usage) => sum + usage.customersTotal, 0);
  }

  // The account usage object only represents billable workspaces, and not unbilled workspaces.
  // If all environments are billed, we can use this number. If some environments are
  // unbilled, however, we need to calculate the total number of people in the account by
  // summing all of the billable people per environment.
  get totalHighWatermarkCustomerCount() {
    if (!this.unbilledEnvironmentUsages.length) {
      return this.highWatermarkCustomersTotal;
    }

    return this.environmentUsages.reduce((sum, usage) => sum + usage.highWatermarkCustomersTotal, 0);
  }

  // The account usage object only represents billable workspaces, and not unbilled workspaces.
  // If all environments are billed, we can use this number. If some environments are
  // unbilled, however, we need to calculate the total number of people in the account by
  // summing all of the billable people per environment.
  get totalObjectCount() {
    if (!this.unbilledEnvironmentUsages.length) {
      return this.objectsTotal;
    }

    return this.environmentUsages.reduce((sum, usage) => sum + usage.objectsTotal, 0);
  }
}

// DO NOT DELETE: this is how TypeScript knows how to look up your models.
declare module 'ember-data/types/registries/model' {
  export default interface ModelRegistry {
    'account-usage': AccountUsageModel;
  }
}
